import { isMapId, mapInfo } from "@69ly/maps";
import { DemoScope, faceitMatchIdIn, MAX_MAPS_PER_MATCH, sameSeries, type BestOf, type DemoSource } from "@69ly/shared";
import { useQueryClient } from "@tanstack/react-query";
import unrarWasm from "node-unrar-js/esm/js/unrar.wasm?url";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { BackLink } from "../components/BackLink.tsx";
import { DropZone } from "../components/DropZone.tsx";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import type { FoundDemo, ImportMessage, ImportRequest, ParsedDemo } from "../demo/import.worker.ts";
import { APP_VERSION } from "../lib/changelog.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { checkDemo, createMatch, matchKeys, replaceReplay, uploadDemo, useDemo, useMatch } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";
import { findFaceitMatch } from "../lib/faceit-queries.ts";
import { mapIconUrl } from "../lib/map-art.ts";
import { useMe, useTeams } from "../lib/queries.ts";
import { MatchFields } from "./Matches.tsx";

export const DEMO_ACCEPT = [".dem", ".gz", ".zst", ".zip", ".rar", "application/gzip", "application/zip", "application/x-rar-compressed", "application/vnd.rar"];
const WRITER = `69LY ${APP_VERSION} · cs2parser 2.7.2`;

type Status = "checking" | "existing" | "queued" | "parsing" | "parsed" | "saving" | "saved" | "replaced" | "failed";

/** A demo found in what was dropped. */
interface Item extends FoundDemo {
  status: Status;
  progress: number;
  existing: { id: string; matchId: string; title: string } | null;
  parsed: ParsedDemo | null;
  error: string | null;
  /** The match it goes into, once read. */
  group: string | null;
  /** Read again to replace the replay kept of it (already in the library). */
  again: boolean;
}

/** A match being put together from the maps read. */
interface Group {
  id: string;
  title: string;
  /** Changed by hand: no longer follows the maps. */
  titleSet: boolean;
  source: DemoSource;
  event: string;
  stage: string;
  bestOf: BestOf;
  bestOfSet: boolean;
  playedAt: string;
  status: "ready" | "saving" | "saved";
  matchId: string | null;
  error: string | null;
  /** The match on FACEIT, once found: it filled the details and marks the match imported. */
  faceit: { state: "looking" | "found" | "none"; matchId: string | null; url: string | null; label: string | null; playedAtSet: boolean } | null;
}

const mb = (n: number) => `${Math.round(n / 1e6)} MB`;
const dateInput = (ms: number) => new Date(ms - new Date(ms).getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
const bestOfFor = (maps: number): BestOf => (maps <= 1 ? 1 : maps <= 3 ? 3 : 5);
let groupSeq = 0;

/** FACEIT, Premier or a scrim, guessed from the file name. */
function guessSource(name: string): DemoSource {
  if (/faceit/i.test(name)) return "faceit";
  if (/^match730_|premier/i.test(name)) return "premier";
  return "other";
}

/**
 * Import demos: drop .dem files (or .gz, .zst, .zip, .rar holding them). Each is read
 * here, in this browser; only its replay (about 1 MB) is sent. Maps of one match
 * (the same players, the same day) are put together, to split or move before saving.
 * A demo already in the library is recognised before it is read, and skipped.
 * With `?match=`, the maps are added to that match instead. With `?again=<demo>`, a map
 * of the library is read again: its demo, dropped here, replaces the replay kept.
 */
export function DemoImportPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe().data;
  const teams = useTeams().data ?? [];
  const { team } = useCurrentTeam();
  const wanted = params.get("to") ?? team?.id ?? null;
  const scope = DemoScope.safeParse(wanted).success ? (wanted as DemoScope) : null;
  const pro = scope === "pro";
  const target = useMatch(params.get("match") ?? undefined).data ?? null;
  const scopeName = pro ? t("demos.proLibrary") : (teams.find((x) => x.id === scope)?.name ?? null);
  const againDemo = useDemo(params.get("again") ?? undefined).data ?? null;
  // The demos the map was read from (a relaunched map has several): any of them dropped is read again.
  const againIds = new Set(againDemo ? againDemo.parts.map((p) => p.id) : []);
  const back = params.get("back") ?? (againDemo ? `/demos/${againDemo.id}` : target ? `/matches/${target.id}` : pro ? "/matches?label=pro" : "/matches");

  const worker = useRef<Worker | null>(null);
  const [opening, setOpening] = useState<File[]>([]);
  const [openErrors, setOpenErrors] = useState<string[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const nextIndex = useRef(0);
  const groupsRef = useRef<Group[]>([]);
  const itemsRef = useRef<Item[]>([]);
  useEffect(() => () => worker.current?.terminate(), []);

  const patch = (index: number, change: Partial<Item>) => setItems((list) => list.map((it) => (it.index === index ? { ...it, ...change } : it)));
  const patchGroup = (id: string, change: Partial<Group>) => setGroups((list) => list.map((g) => (g.id === id ? { ...g, ...change } : g)));

  const getWorker = () => {
    if (worker.current) return worker.current;
    const w = new Worker(new URL("../demo/import.worker.ts", import.meta.url), { type: "module" });
    worker.current = w;
    w.onmessage = (e: MessageEvent<ImportMessage>) => handler.current(e.data);
    // The worker died (out of memory most often): say so on what it was doing, and start a
    // new one for what is left.
    w.onerror = (e) => {
      e.preventDefault();
      worker.current = null;
      w.terminate();
      handler.current({ type: "crashed" });
    };
    return w;
  };

  // The worker does one thing at a time (a dropped file is unpacked, or a demo read): each
  // demo is a few hundred MB, an HLTV archive often over a GB. So one file at a time: its
  // demos are read, then the next file is opened (opening every archive first ran the
  // browser out of memory with two .rar files).
  const [openingNow, setOpeningNow] = useState(false);
  const parsing = items.some((it) => it.status === "parsing");
  const checking = items.some((it) => it.status === "checking");
  const next = items.find((it) => it.status === "queued");
  useEffect(() => {
    if (openingNow || parsing) return;
    if (next) {
      patch(next.index, { status: "parsing", progress: 0 });
      getWorker().postMessage({ type: "parse", index: next.index, writer: WRITER } satisfies ImportRequest);
      return;
    }
    if (checking || opening.length === 0) return;
    const [file, ...rest] = opening;
    setOpening(rest);
    setOpeningNow(true);
    const base = nextIndex.current;
    nextIndex.current += 100;
    getWorker().postMessage({ type: "open", file, base, wasm: { unrar: unrarWasm } } satisfies ImportRequest);
  }, [openingNow, parsing, checking, next, opening]);

  function onMessage(m: ImportMessage | { type: "crashed" }) {
    if (m.type === "crashed") {
      const busy = itemsRef.current.find((it) => it.status === "parsing");
      if (busy) patch(busy.index, { status: "failed", error: t("demos.import.errors.crashed") });
      else if (openingNow) setOpenErrors((e) => [...e, t("demos.import.errors.crashed")]);
      setOpeningNow(false);
      return;
    }
    if (m.type === "found") {
      setOpeningNow(false);
      setItems((list) => [...list, ...m.demos.map((d) => ({ ...d, status: "checking" as const, progress: 0, existing: null, parsed: null, error: null, group: null, again: false }))]);
      for (const d of m.demos)
        checkDemo(scope!, d.fingerprint)
          .then((c) =>
            patch(
              d.index,
              // Dropped to read a map again: it is read again straight away.
              c.existing
                ? againIds.has(c.existing.id)
                  ? { status: "queued", existing: c.existing, again: true }
                  : { status: "existing", existing: c.existing }
                : againDemo
                  ? { status: "failed", error: t("demos.import.notThis", { file: againDemo.fileName }) }
                  : { status: "queued" },
            ),
          )
          .catch(() => patch(d.index, { status: "queued" }));
    } else if (m.type === "progress") patch(m.index, { progress: m.fraction });
    else if (m.type === "parsed") {
      const list = itemsRef.current;
      const it = list.find((x) => x.index === m.demo.index);
      if (!it) return;
      if (it.again && it.existing) {
        // Read again: its new replay replaces the one kept; it joins no match.
        const existing = it.existing;
        patch(it.index, { status: "saving", progress: 0 });
        replaceReplay(existing.id, m.demo.replay)
          .then(() => {
            patch(it.index, { status: "replaced" });
            void qc.invalidateQueries({ queryKey: matchKeys.all });
          })
          .catch((error: unknown) => patch(it.index, { status: "failed", error: errorText(t, error) }));
        return;
      }
      const done = { ...it, status: "parsed" as const, parsed: m.demo };
      const group = target ? "target" : placeInGroup(done, list);
      itemsRef.current = list.map((x) => (x.index === it.index ? { ...done, group } : x));
      setItems((now) => now.map((x) => (x.index === it.index ? { ...x, status: "parsed", parsed: m.demo, group } : x)));
    } else if (m.type === "error") {
      if (m.index === null) {
        setOpeningNow(false);
        const why = t(`demos.import.errors.${m.code}`, { defaultValue: t("demos.import.errors.unpack_failed") });
        setOpenErrors((e) => [...e, m.from ? `${m.from}: ${why}` : why]);
      } else patch(m.index, { status: "failed", error: t(`demos.import.errors.${m.code}`, { defaultValue: t("demos.import.errors.parse_failed") }) });
    }
  }

  /** The match a newly read map joins: one not saved yet with the same players that day, or a new one. */
  function placeInGroup(it: Item, list: Item[]): string {
    const parsed = it.parsed!;
    const candidate = { players: parsed.players, playedAt: it.playedAt };
    const open = groupsRef.current.filter((g) => g.status === "ready");
    for (const g of open) {
      const members = list.filter((x) => x.group === g.id && x.parsed);
      if (members.length >= MAX_MAPS_PER_MATCH) continue;
      if (members.some((x) => sameSeries({ players: x.parsed!.players, playedAt: x.playedAt }, candidate))) return g.id;
    }
    const id = `g${++groupSeq}`;
    const g: Group = {
      id,
      title: `${parsed.teams[0]} vs ${parsed.teams[1]}`,
      titleSet: false,
      source: pro ? "pro" : guessSource(`${it.from} ${it.name}`),
      event: "",
      stage: "",
      bestOf: 1,
      bestOfSet: false,
      playedAt: dateInput(it.playedAt),
      status: "ready",
      matchId: null,
      error: null,
      faceit: null,
    };
    groupsRef.current = [...groupsRef.current, g];
    setGroups(groupsRef.current);
    // A FACEIT demo (by its file name): its match's details come from FACEIT.
    if (!pro && (g.source === "faceit" || faceitMatchIdIn(`${it.from} ${it.name}`))) void fillFromFaceit(id, it);
    return id;
  }

  /** Looks the group's match up on FACEIT (by the id in the file name, else by its players and date) and fills what was not typed. */
  async function fillFromFaceit(groupId: string, it: Item) {
    patchGroup(groupId, { faceit: { state: "looking", matchId: null, url: null, label: null, playedAtSet: false } });
    const fill = await findFaceitMatch({
      scope: pro ? null : scope,
      matchId: faceitMatchIdIn(it.name) ?? faceitMatchIdIn(it.from),
      steamIds: it.parsed?.players.map((p) => p.steamId).filter((x) => /^\d{17}$/.test(x)) ?? [],
      playedAt: new Date(it.playedAt).toISOString(),
    });
    setGroups((list) =>
      list.map((g) => {
        if (g.id !== groupId) return g;
        if (!fill) return { ...g, faceit: { state: "none", matchId: null, url: null, label: null, playedAtSet: false } };
        const label = [fill.event, fill.stage].filter(Boolean).join(" · ");
        return {
          ...g,
          title: g.titleSet ? g.title : fill.title,
          source: "faceit",
          event: g.event.trim() ? g.event : fill.event,
          stage: g.stage.trim() ? g.stage : (fill.stage ?? ""),
          bestOf: g.bestOfSet ? g.bestOf : fill.bestOf,
          bestOfSet: g.bestOfSet || fill.bestOf > 1,
          playedAt: fill.playedAt ? dateInput(Date.parse(fill.playedAt)) : g.playedAt,
          faceit: { state: "found", matchId: fill.match.id, url: fill.match.url, label, playedAtSet: !!fill.playedAt },
        };
      }),
    );
  }
  const handler = useRef(onMessage);
  useEffect(() => {
    handler.current = onMessage;
    groupsRef.current = groups;
    itemsRef.current = items;
  });

  // A match follows its maps until changed by hand: best of, the date of its first map.
  useEffect(() => {
    setGroups((list) => {
      let changed = false;
      const out = list.map((g) => {
        const maps = items.filter((x) => x.group === g.id);
        if (g.status !== "ready" || maps.length === 0) return g;
        const bestOf = g.bestOfSet ? g.bestOf : (Math.max(g.bestOf, bestOfFor(maps.length)) as BestOf);
        const playedAt = g.faceit?.playedAtSet ? g.playedAt : dateInput(Math.min(...maps.map((x) => x.playedAt)));
        if (bestOf === g.bestOf && playedAt === g.playedAt) return g;
        changed = true;
        return { ...g, bestOf, playedAt };
      });
      return changed ? out : list;
    });
  }, [items]);

  const mapsOf = (g: Group) => items.filter((x) => x.group === g.id).sort((a, b) => a.playedAt - b.playedAt || a.name.localeCompare(b.name));

  /** Takes a map out into a match of its own, or moves it into another one. */
  function moveTo(it: Item, groupId: string | "new") {
    let id = groupId;
    if (groupId === "new") {
      const from = groups.find((g) => g.id === it.group);
      id = `g${++groupSeq}`;
      const g: Group = {
        ...(from as Group),
        id,
        title: from ? from.title : it.name,
        bestOf: 1,
        bestOfSet: false,
        playedAt: dateInput(it.playedAt),
        matchId: null,
        error: null,
        status: "ready",
        faceit: null,
      };
      groupsRef.current = [...groupsRef.current, g];
      setGroups(groupsRef.current);
    }
    patch(it.index, { group: id });
  }

  async function save(g: Group) {
    if (!scope) return;
    const maps = mapsOf(g).filter((x) => x.status === "parsed" || x.status === "failed");
    if (maps.length === 0) return;
    patchGroup(g.id, { status: "saving", error: null });
    let matchId = g.matchId ?? target?.id ?? null;
    try {
      if (!matchId) {
        const created = await createMatch({
          scope,
          title: g.title.trim() || maps[0].name,
          source: pro ? "pro" : g.source,
          event: g.event.trim() || null,
          stage: g.stage.trim() || null,
          bestOf: g.bestOf,
          playedAt: g.playedAt ? new Date(`${g.playedAt}T12:00:00`).toISOString() : null,
          faceitMatchId: g.faceit?.matchId ?? null,
        });
        matchId = created.id;
        patchGroup(g.id, { matchId });
      }
    } catch (error) {
      patchGroup(g.id, { status: "ready", error: errorText(t, error) });
      return;
    }
    let failed = false;
    for (const it of maps) {
      if (!it.parsed) continue;
      patch(it.index, { status: "saving", progress: 0, error: null });
      try {
        await uploadDemo({ matchId, fileName: it.name, fingerprint: it.fingerprint }, it.parsed.replay, (done) => patch(it.index, { progress: done }));
        patch(it.index, { status: "saved" });
      } catch (error) {
        failed = true;
        patch(it.index, { status: "failed", error: errorText(t, error) });
      }
    }
    void qc.invalidateQueries({ queryKey: matchKeys.all });
    patchGroup(g.id, failed ? { status: "ready", error: t("demos.import.someFailed") } : { status: "saved" });
  }

  const reading = items.filter((x) => !x.group);
  const ready = groups.filter((g) => g.status === "ready" && mapsOf(g).some((x) => x.status === "parsed"));
  const saved = groups.filter((g) => g.status === "saved");
  const working = opening.length > 0 || openingNow || parsing || items.some((x) => x.status === "queued" || x.status === "checking");
  const targetMaps = target ? items.filter((x) => x.group === "target") : [];

  if (!scope)
    return (
      <div className="page page-narrow">
        <BackLink fallback="/matches" fallbackLabel={t("matches.title")} />
        <div className="card">
          <h1 className="section-title">{t("demos.import.title")}</h1>
          <p className="muted">{t("demos.teamOnly")}</p>
          <Link to="/team" className="btn btn-secondary">
            {t("demos.goTeam")}
          </Link>
        </div>
      </div>
    );

  return (
    <div className="page page-narrow demo-import">
      <BackLink fallback={back} fallbackLabel={againDemo ? t("demos.import.againBack") : target ? target.title : t("matches.title")} />
      {againDemo ? (
        <header>
          <h1 className="page-title">{t("demos.import.againTitle")}</h1>
          <p className="muted">{t("demos.import.againIntro", { file: againDemo.fileName, title: againDemo.match.title })}</p>
        </header>
      ) : (
        <header>
          <h1 className="page-title">{target ? t("demos.import.addTitle") : t("demos.import.title")}</h1>
          <p className="muted">{target ? t("demos.import.addInto", { title: target.title }) : t("demos.import.into", { library: scopeName ?? "" })}</p>
        </header>
      )}

      {pro && !me?.isAdmin ? (
        <p className="field-error">{t("demos.import.adminsOnly")}</p>
      ) : (
        <DropZone
          accept={DEMO_ACCEPT}
          multiple
          hint={t("demos.import.drop")}
          onFiles={(files) => setOpening((o) => [...o, ...files])}
          onRefused={(names) => setOpenErrors((e) => [...e, t("demos.import.errors.not_a_demo_named", { names: names.join(", ") })])}
        >
          <div className="demo-drop">
            <Icon name="upload" size={28} />
            <p>
              <b>{t("demos.import.dropMany")}</b>
            </p>
            <p className="muted small">{t("demos.import.formats")}</p>
            <label className="btn btn-secondary file-button">
              {t("demos.import.pick")}
              <input
                type="file"
                multiple
                accept={DEMO_ACCEPT.join(",")}
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])];
                  e.target.value = "";
                  if (files.length) setOpening((o) => [...o, ...files]);
                }}
              />
            </label>
            <p className="muted small">{t("demos.import.private")}</p>
          </div>
        </DropZone>
      )}

      {(openingNow || opening.length > 0) && (
        <p className="muted" role="status">
          {t("demos.import.openingMany", { count: opening.length + (openingNow ? 1 : 0) })}
        </p>
      )}
      {openErrors.map((e) => (
        <p key={e} className="field-error">
          {e}
        </p>
      ))}

      {reading.length > 0 && (
        <section aria-labelledby="import-reading">
          <h2 id="import-reading" className="eyebrow">
            {t("demos.import.readingTitle")}
          </h2>
          <ul className="demo-import-list">
            {reading.map((it) => (
              <ReadingRow key={it.index} it={it} onAgain={() => patch(it.index, { status: "queued", again: true })} />
            ))}
          </ul>
        </section>
      )}

      {target && targetMaps.length > 0 && (
        <section className="card demo-import-match">
          <h2 className="section-title">{t("demos.import.addTo", { title: target.title })}</h2>
          <ul className="demo-import-maps">
            {targetMaps.map((it) => (
              <MapRow key={it.index} it={it} />
            ))}
          </ul>
          {targetMaps.some((x) => x.status === "parsed" || x.status === "failed") && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={targetMaps.some((x) => x.status === "saving")}
              onClick={() => {
                const g: Group = {
                  id: "target",
                  title: target.title,
                  titleSet: true,
                  source: target.source,
                  event: "",
                  stage: "",
                  bestOf: target.bestOf,
                  bestOfSet: true,
                  playedAt: "",
                  status: "ready",
                  matchId: target.id,
                  error: null,
                  faceit: null,
                };
                void save(g);
              }}
            >
              {t("demos.import.addMaps", { count: targetMaps.filter((x) => x.status === "parsed").length })}
            </button>
          )}
          {targetMaps.length > 0 && targetMaps.every((x) => x.status === "saved") && (
            <Link to={`/matches/${target.id}`} className="btn btn-secondary">
              {t("demos.import.openMatch")}
            </Link>
          )}
        </section>
      )}

      {groups.map((g) => {
        const maps = mapsOf(g);
        if (maps.length === 0) return null;
        const others = groups.filter((o) => o.id !== g.id && o.status === "ready" && mapsOf(o).length < MAX_MAPS_PER_MATCH);
        return (
          <section key={g.id} className={`card demo-import-match is-${g.status}`} aria-label={g.title}>
            <div className="demo-import-match-head">
              <span className="eyebrow">{t("demos.import.matchOf", { count: maps.length })}</span>
              {g.status === "saved" && g.matchId && (
                <Link to={`/matches/${g.matchId}`} className="btn btn-secondary btn-small">
                  {t("demos.import.openMatch")}
                </Link>
              )}
            </div>
            {g.status !== "saved" ? (
              <MatchFields
                title={g.title}
                setTitle={(v) => patchGroup(g.id, { title: v, titleSet: true })}
                source={pro ? null : g.source}
                setSource={(v) => patchGroup(g.id, { source: v })}
                event={g.event}
                setEvent={(v) => patchGroup(g.id, { event: v })}
                stage={g.stage}
                setStage={(v) => patchGroup(g.id, { stage: v })}
                bestOf={g.bestOf}
                setBestOf={(v) => patchGroup(g.id, { bestOf: v, bestOfSet: true })}
                playedAt={g.playedAt}
                setPlayedAt={(v) => patchGroup(g.id, { playedAt: v })}
              />
            ) : null}
            {g.status === "saved" ? (
              <p className="ok-text">{t("demos.import.savedMatch", { title: g.title })}</p>
            ) : !pro ? (
              <FaceitLine
                faceit={g.faceit}
                canLook={g.source === "faceit" && !!maps.find((x) => x.parsed)}
                onLook={() => {
                  const first = maps.find((x) => x.parsed);
                  if (first) void fillFromFaceit(g.id, first);
                }}
              />
            ) : null}
            <ol className="demo-import-maps">
              {maps.map((it) => (
                <MapRow key={it.index} it={it}>
                  {g.status === "ready" && it.status === "parsed" && (maps.length > 1 || others.length > 0) && (
                    <Select
                      label={t("demos.import.moveLabel", { name: it.name })}
                      value={null}
                      placeholder={t("demos.import.move")}
                      groups={[
                        {
                          options: [
                            ...(maps.length > 1 ? [{ value: "new", label: t("demos.import.split") }] : []),
                            ...others.map((o) => ({ value: o.id, label: t("demos.import.moveInto", { title: o.title }) })),
                          ],
                        },
                      ]}
                      onChange={(v) => moveTo(it, v)}
                    />
                  )}
                </MapRow>
              ))}
            </ol>
            {g.error && <p className="field-error">{g.error}</p>}
            {g.status !== "saved" && (
              <div className="row">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={g.status === "saving" || !g.title.trim() || !maps.some((x) => x.status === "parsed" || x.status === "failed")}
                  onClick={() => void save(g)}
                >
                  {g.status === "saving" ? t("demos.import.sending") : t("demos.import.saveMatch", { count: maps.filter((x) => x.status === "parsed" || x.status === "failed").length })}
                </button>
              </div>
            )}
          </section>
        );
      })}

      {ready.length > 1 && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            void (async () => {
              for (const g of ready) await save(g);
            })();
          }}
        >
          {t("demos.import.saveAllMatches", { count: ready.length })}
        </button>
      )}
      {saved.length > 0 && ready.length === 0 && !working && (
        <button type="button" className="btn btn-secondary" onClick={() => void navigate(saved.length === 1 && saved[0].matchId ? `/matches/${saved[0].matchId}` : back)}>
          {saved.length === 1 ? t("demos.import.openMatch") : t("demos.import.done")}
        </button>
      )}
    </div>
  );
}

/** A demo still being opened, checked or read, or that could not be. */
function ReadingRow({ it, onAgain }: { it: Item; onAgain: () => void }) {
  const { t } = useTranslation();
  return (
    <li className={`card demo-import-item is-${it.status}`}>
      <div className="demo-import-head">
        <div>
          <b className="file-name">{it.name}</b>
          <span className="muted small"> · {mb(it.size)}</span>
        </div>
      </div>
      {it.status === "checking" && <p className="muted small">{t("demos.import.checking")}</p>}
      {it.status === "queued" && <p className="muted small">{t("demos.import.queued")}</p>}
      {it.status === "parsing" && (
        <div className="demo-progress">
          <progress value={it.progress} max={1} aria-label={t("demos.import.reading")} />
          <span className="small muted">{t("demos.import.readingPct", { pct: Math.round(it.progress * 100) })}</span>
        </div>
      )}
      {it.status === "existing" && it.existing && (
        <div className="demo-import-again">
          <p className="small">
            {t("demos.import.already")} <Link to={`/matches/${it.existing.matchId}`}>{it.existing.title}</Link>
          </p>
          <button type="button" className="btn btn-quiet btn-small" title={t("demos.import.againHelp")} onClick={onAgain}>
            <Icon name="loop" size={14} />
            {t("demos.import.again")}
          </button>
        </div>
      )}
      {it.status === "saving" && it.again && (
        <div className="demo-progress">
          <progress aria-label={t("demos.import.sending")} />
          <span className="small muted">{t("demos.import.sending")}</span>
        </div>
      )}
      {it.status === "replaced" && it.existing && (
        <p className="small ok-text">
          {t("demos.import.replaced")} <Link to={`/demos/${it.existing.id}`}>{t("demos.watch")}</Link>
        </p>
      )}
      {it.status === "failed" && <p className="field-error small">{it.error}</p>}
    </li>
  );
}

/** Where the match stands with FACEIT: being looked up, found (filled from it), or not found. */
function FaceitLine({ faceit, canLook, onLook }: { faceit: Group["faceit"]; canLook: boolean; onLook: () => void }) {
  const { t } = useTranslation();
  if (faceit?.state === "looking")
    return (
      <p className="muted small faceit-fill" role="status">
        {t("demos.import.faceit.looking")}
      </p>
    );
  if (faceit?.state === "found")
    return (
      <p className="small faceit-fill is-found" role="status">
        <Icon name="check" size={14} />
        {t("demos.import.faceit.found", { label: faceit.label || t("faceit.title") })}
        {faceit.url && (
          <a href={faceit.url} target="_blank" rel="noreferrer">
            {t("faceit.room")} <Icon name="external" size={12} />
          </a>
        )}
      </p>
    );
  return (
    <p className="muted small faceit-fill">
      {faceit?.state === "none" && <span role="status">{t("demos.import.faceit.none")} </span>}
      {canLook && (
        <button type="button" className="btn btn-quiet btn-small" onClick={onLook}>
          {faceit?.state === "none" ? t("demos.import.faceit.again") : t("demos.import.faceit.look")}
        </button>
      )}
    </p>
  );
}

/** A map read and ready (or being sent): map, teams, score, file. */
function MapRow({ it, children }: { it: Item; children?: ReactNode }) {
  const { t } = useTranslation();
  const p = it.parsed!;
  return (
    <li className={`demo-import-map is-${it.status}`}>
      {isMapId(p.map) && <img src={mapIconUrl(p.map)} alt="" width={36} height={36} />}
      <div className="demo-import-map-main">
        <span>
          <b>{isMapId(p.map) ? mapInfo(p.map).name : p.map}</b> · {p.teams[0]} <b className="mono">{p.score[0]}</b> : <b className="mono">{p.score[1]}</b> {p.teams[1]}
        </span>
        <span className="muted small">
          <span className="file-name">{it.name}</span> · {t("demos.rounds", { count: p.rounds })} · {t("demos.readIn", { s: p.parseSeconds })}
        </span>
        {it.status === "saving" && (
          <div className="demo-progress">
            <progress value={it.progress} max={1} aria-label={t("demos.import.sending")} />
            <span className="small muted">{t("demos.import.sendingPct", { pct: Math.round(it.progress * 100) })}</span>
          </div>
        )}
        {it.status === "saved" && <span className="ok-text small">{t("demos.import.saved")}</span>}
        {it.error && <span className="field-error small">{it.error}</span>}
      </div>
      {children}
    </li>
  );
}
